/* eslint-disable @typescript-eslint/no-empty-interface */
import { Organization } from "@/types";
import { createTheme, PaletteColorOptions } from "@mui/material";
import { useMemo } from "react";

const { palette } = createTheme();
const { augmentColor } = palette;

const createColor = (mainColor: string) => augmentColor({ color: { main: mainColor } });

declare module "@mui/material/styles" {
  interface CustomPalette {
    white: PaletteColorOptions;
  }
  interface Palette extends CustomPalette {}
  interface PaletteOptions extends CustomPalette {}
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    white: true;
  }
}

const useOrganizationTheme = (organization?: Organization) => {
  const theme = useMemo(() => {
    if (!organization) return createTheme({});

    const draft = createTheme({
      typography: {
        fontFamily: ["Fira Sans"].join(","),
        fontSize: 16,
      },
      shape: {
        borderRadius: 8,
      },
      palette: {
        white: createColor("#ffffff"),
      },
      components: {
        MuiTypography: {
          styleOverrides: {
            h1: {
              fontSize: 36,
              fontWeight: 800,
            },
            h2: {
              fontSize: 28,
              fontWeight: 500,
            },
            h3: {
              fontSize: 24,
            },
            h4: {
              fontSize: 18,
            },
            h5: {
              fontSize: 16,
            },
            h6: {
              fontSize: 14,
            },
            body1: {
              fontWeight: 300,
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              fontSize: 28,
            },
          },
        },
        MuiButton: {
          defaultProps: {
            disableElevation: true,
            variant: "contained",
          },
          styleOverrides: {
            root: {
              borderRadius: "8px",
              textTransform: "unset",
              fontWeight: 300,
            },
          },
        },
        MuiPaper: {
          defaultProps: {
            elevation: 2,
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              backgroundColor: "white",
              borderRadius: "4px !important",
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            colorDefault: {
              backgroundColor: "white",
            },
          },
        },
      },
    });

    if (organization?.primaryColor) {
      draft.palette.primary = createColor(organization.primaryColor);
    }
    if (organization?.secondaryColor) {
      draft.palette.secondary = createColor(organization.secondaryColor);
    }

    return draft;
  }, [organization]);

  return theme;
};

export default useOrganizationTheme;
